import { mediaQueryLarge, prefersReducedMotion, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/** Below this many visible pixels of the results column, a filter change scrolls the results back into view. */
const MIN_VISIBLE_RESULTS = 160;
/** Gap kept between the sticky header and the top of the results after that scroll. */
const RESULTS_SCROLL_GAP = 16;
/** The rendered results: product grid, quick order app block, or the "no products" message. */
const RESULTS_CONTENT_SELECTOR = '.product-grid, .quick-order-slot > *, .main-collection-grid__empty';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdateScroll);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdateScroll);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleDesignModeBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#handleDesignModeBlockDeselect);
    }

    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdateScroll);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdateScroll);
    this.removeEventListener('shopify:block:select', this.#handleDesignModeBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleDesignModeBlockDeselect);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event: falls back to the viewport's default
   * option (the one carrying data-grid-layout), which may be the quick order view.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout(targetElement.value);
  };

  /**
   * Brings the results back into view after a filter change.
   *
   * The filter sidebar is often taller than a filtered result set: a visitor who scrolled
   * down to a lower filter group and lands on a couple of products is left looking at
   * empty space beside the filters, with the products above the fold. Once the section
   * has re-rendered, scroll the page container so the results start under the sticky
   * header — but only when (almost) none of the results column is visible, so filtering
   * within a long list never yanks the page around.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleFilterUpdateScroll = async (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest?.('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;

    try {
      await event.promise;
    } catch {
      return;
    }

    // Let the morphed section paint before measuring it.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (!this.isConnected) return;

    // Measure the rendered results themselves (product grid, quick order form or the empty
    // state), not the results column: the column is stretched to the sidebar's height.
    const rects = [...this.querySelectorAll(RESULTS_CONTENT_SELECTOR)]
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.height > 0);
    if (rects.length === 0) return;

    const top = Math.min(...rects.map((rect) => rect.top));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));
    const headerOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const visible = Math.min(bottom, window.innerHeight) - Math.max(top, headerOffset);

    if (visible >= MIN_VISIBLE_RESULTS) return;

    scrollTo({
      top: Math.max(0, getScrollTop() + top - headerOffset - RESULTS_SCROLL_GAP),
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  };

  /**
   * Previews the quick order view when one of its blocks is selected in the theme editor.
   *
   * @param {Event} event
   */
  #handleDesignModeBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-order-slot')) return;

    this.#setLayout('quick-order');
  };

  /**
   * Restores the chosen layout when a quick order block is deselected in the theme editor.
   *
   * @param {Event} event
   */
  #handleDesignModeBlockDeselect = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-order-slot')) return;

    const name = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
    const checked = this.querySelector(`input[name="${name}"]:checked`);
    this.#setLayout(checked instanceof HTMLInputElement ? checked.value : 'default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
